import axios from "axios";
// ---- Django CSRF integration (centralized) ----
// Make axios compatible with Django's CSRF expectations
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";
axios.defaults.withCredentials = true;

import API_BASE_URL from "config/api"; // 路径按你的实际调整

const AUTH_BYPASS_PATHS = ["/auth/login/", "/auth/signup/", "/auth/refresh/", "/auth/csrf/"];

const shouldBypassAuthHeader = (url) => {
  if (!url || typeof url !== "string") return false;
  return AUTH_BYPASS_PATHS.some((path) => url.includes(path));
};

// ---- JWT helpers (centralized) ----
const readStorageValue = (key) => {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
};

const getAccessToken = () => {
  try {
    return (
      readStorageValue("access_token") ||
      readStorageValue("accessToken") ||
      readStorageValue("access") ||
      readStorageValue("jwt_access") ||
      ""
    );
  } catch (e) {
    return "";
  }
};

const getRefreshToken = () => {
  try {
    return (
      readStorageValue("refresh_token") ||
      readStorageValue("refreshToken") ||
      readStorageValue("refresh") ||
      readStorageValue("jwt_refresh") ||
      ""
    );
  } catch (e) {
    return "";
  }
};

const getAuthStorage = () => {
  try {
    if (
      localStorage.getItem("auth_persistence") === "local" ||
      localStorage.getItem("refresh_token")
    ) {
      return localStorage;
    }
    if (
      sessionStorage.getItem("auth_persistence") === "session" ||
      sessionStorage.getItem("refresh_token")
    ) {
      return sessionStorage;
    }
  } catch (e) {
    // ignore
  }
  return localStorage;
};

const saveTokens = ({ access, refresh }) => {
  try {
    const storage = getAuthStorage();
    if (access) {
      storage.setItem("access_token", access);
      storage.setItem("access", access); // compatibility
    }
    if (refresh) {
      storage.setItem("refresh_token", refresh);
      storage.setItem("refresh", refresh); // compatibility
    }
  } catch (e) {
    // ignore
  }
};
const clearTokens = () => {
  try {
    const keys = [
      "access_token",
      "refresh_token",
      "access",
      "refresh",
      "accessToken",
      "refreshToken",
      "jwt_access",
      "jwt_refresh",
      "token",
      "jwt",
      "jwt_token",
      "auth_token",
      "user",
      "auth_persistence",
    ];
    keys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  } catch (e) {
    // ignore
  }
};

// ---- Refresh single-flight (avoid multiple refresh calls in parallel) ----
let refreshInFlight = null;

async function refreshAccessTokenSingleFlight() {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const refreshToken = typeof window !== "undefined" ? getRefreshToken() : "";

    if (!refreshToken) {
      throw new Error("missing_refresh_token");
    }

    // Use a plain axios call (NOT the `api` instance) to avoid interceptor loops.
    const refreshResponse = await axios.post(`${API_BASE_URL}/web2/auth/refresh/`, {
      refresh: refreshToken,
    });

    const data = refreshResponse.data || {};
    const newAccessToken = data.access;
    const newRefreshToken = data.refresh || refreshToken;

    if (!newAccessToken) {
      throw new Error("missing_new_access_token");
    }

    if (typeof window !== "undefined") {
      saveTokens({ access: newAccessToken, refresh: newRefreshToken });
    }

    return { access: newAccessToken, refresh: newRefreshToken };
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

// Ensure CSRF cookie is set (call once before first POST if needed)
export async function ensureCsrfCookie() {
  // This endpoint should be a GET view decorated with @ensure_csrf_cookie
  // If you already have any GET that sets csrftoken, you may skip calling this.
  try {
    await axios.get(`${API_BASE_URL}/web2/auth/csrf/`);
  } catch (e) {
    // ignore
  }
}

// Global interceptor for any direct `axios.*` calls (not only the `api` instance)
axios.interceptors.request.use(
  (config) => {
    if (shouldBypassAuthHeader(config?.url)) {
      return config;
    }

    if (typeof window !== "undefined") {
      const token = getAccessToken();
      if (token) {
        if (!config.headers) config.headers = {};
        // Do not overwrite if caller explicitly set Authorization
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);
// Global response interceptor: if someone uses plain axios instead of `api`, still try refresh-on-401.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (!error.response) return Promise.reject(error);
    if (shouldBypassAuthHeader(originalRequest?.url)) {
      return Promise.reject(error);
    }
    if (error.response.status !== 401 || originalRequest?._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      const tokens = await refreshAccessTokenSingleFlight();
      if (!originalRequest.headers) originalRequest.headers = {};
      originalRequest.headers.Authorization = `Bearer ${tokens.access}`;
      return axios(originalRequest);
    } catch (refreshError) {
      if (typeof window !== "undefined") {
        clearTokens();
        window.location.href = "/signin/";
      }
      return Promise.reject(refreshError);
    }
  }
);

const api = axios.create({
  baseURL: `${API_BASE_URL}/web2/`,
  // 你 Django 后端外层代理后的真实域名也可以写这里
});

// 请求拦截器：自动附带 access token（JWT）
api.interceptors.request.use(
  (config) => {
    if (shouldBypassAuthHeader(config?.url)) {
      return config;
    }

    if (typeof window !== "undefined") {
      const token = getAccessToken();
      if (token) {
        if (!config.headers) config.headers = {};
        if (!config.headers.Authorization && !config.headers.authorization) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器：遇到 401 尝试使用 refresh_token 续期，并重试原请求
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 没有响应（网络错误等）或没有状态码，直接抛出
    if (!error.response) {
      return Promise.reject(error);
    }
    if (shouldBypassAuthHeader(originalRequest?.url)) {
      return Promise.reject(error);
    }

    // 如果不是 401，或者已经重试过一次，则直接抛出
    if (error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    // 标记这次请求已经进入刷新流程，避免死循环
    originalRequest._retry = true;

    try {
      const tokens = await refreshAccessTokenSingleFlight();

      // 更新当前请求的 Authorization 头
      if (!originalRequest.headers) {
        originalRequest.headers = {};
      }
      originalRequest.headers.Authorization = `Bearer ${tokens.access}`;

      // 使用更新后的 token 重试原请求
      return api(originalRequest);
    } catch (refreshError) {
      // 刷新失败：清理本地 token，并强制跳转到登录页
      if (typeof window !== "undefined") {
        clearTokens();
        window.location.href = "/signin/";
      }
      return Promise.reject(refreshError);
    }
  }
);

export default api;
